import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {useAuth} from '@/hooks/platform/use-auth'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'

export type PartnerQuota = components['schemas']['PartnerQuotaRead']
export type PartnerBusinessRow = components['schemas']['PartnerBusinessRow']
export type PartnerActivity = components['schemas']['PartnerActivityRead']
export type PartnerInvoice = components['schemas']['PartnerInvoiceRead']
export type PartnerMe = components['schemas']['PartnerMeRead']
type PartnerBillingCycleRead = components['schemas']['PartnerBillingCycleRead']
export type PartnerBillingCycle = PartnerBillingCycleRead['billing_cycle_preferido']

export const PARTNER_PANEL_PAGE_SIZE = 20

export function useIsPartner() {
  const {user} = useAuth()
  return user?.is_partner ?? false
}

export function usePartnerMe(enabled = true) {
  return useQuery({
    queryKey: ['partner', 'me'],
    queryFn: async () => (await api.get<PartnerMe>('/partner/me')).data,
    enabled,
  })
}

export function usePartnerQuota(enabled = true) {
  return useQuery({
    queryKey: ['partner', 'quota'],
    queryFn: async () => (await api.get<PartnerQuota>('/partner/quota')).data,
    enabled,
  })
}

export function usePartnerPanelBusinesses(params: {page: number; search: string}, enabled = true) {
  return useQuery({
    queryKey: ['partner', 'businesses', params],
    queryFn: async () =>
      (
        await api.get<Page<PartnerBusinessRow>>('/partner/businesses', {
          params: {page: params.page, page_size: PARTNER_PANEL_PAGE_SIZE, search: params.search || undefined},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function usePartnerPendingBusinesses(params: {page: number}, enabled = true) {
  return useQuery({
    queryKey: ['partner', 'businesses', 'pendientes', params],
    queryFn: async () =>
      (
        await api.get<Page<PartnerBusinessRow>>('/partner/businesses/pendientes', {
          params: {page: params.page, page_size: PARTNER_PANEL_PAGE_SIZE},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

type PartnerApproval = components['schemas']['PartnerApprovalRead']
type PartnerRejection = components['schemas']['PartnerRejectionRead']

function invalidatePartnerCartera(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({queryKey: ['partner', 'businesses']})
  queryClient.invalidateQueries({queryKey: ['partner', 'quota']})
  queryClient.invalidateQueries({queryKey: ['partner', 'activity']})
  queryClient.invalidateQueries({queryKey: ['partner', 'invoices']})
}

export function useApprovePartnerBusiness() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (businessId: string) =>
      (await api.post<PartnerApproval>(`/partner/businesses/${businessId}/aprobar`)).data,
    onSuccess: () => invalidatePartnerCartera(queryClient),
  })
}

export function useRejectPartnerBusiness() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (businessId: string) =>
      (await api.post<PartnerRejection>(`/partner/businesses/${businessId}/rechazar`)).data,
    onSuccess: () => invalidatePartnerCartera(queryClient),
  })
}

export function usePartnerActivity(params: {page: number}, enabled = true) {
  return useQuery({
    queryKey: ['partner', 'activity', params],
    queryFn: async () =>
      (
        await api.get<Page<PartnerActivity>>('/partner/activity', {
          params: {page: params.page, page_size: PARTNER_PANEL_PAGE_SIZE},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

// Las facturas propias del partner: Kairos nunca le muestra precio a su cliente (pricing-spec decisión 1).
export function usePartnerInvoices(params: {page: number}, enabled = true) {
  return useQuery({
    queryKey: ['partner', 'invoices', params],
    queryFn: async () =>
      (
        await api.get<Page<PartnerInvoice>>('/partner/invoices', {
          params: {page: params.page, page_size: PARTNER_PANEL_PAGE_SIZE},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function usePartnerBillingCycle(enabled = true) {
  return useQuery({
    queryKey: ['partner', 'billing-cycle'],
    queryFn: async () => (await api.get<PartnerBillingCycleRead>('/partner/billing-cycle')).data,
    enabled,
  })
}

export function useUpdatePartnerBillingCycle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (billingCyclePreferido: PartnerBillingCycle) =>
      (
        await api.patch<PartnerBillingCycleRead>('/partner/billing-cycle', {
          billing_cycle_preferido: billingCyclePreferido,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partner', 'billing-cycle']})
    },
  })
}

// PUT y DELETE exigen `require_partner_writable`: la vista «ver como partner» solo lee.
export type PartnerPaymentSourceConfig = components['schemas']['PartnerPaymentSourceConfigRead']
export type PartnerPaymentSourcePayload = components['schemas']['PartnerPaymentSourceCreate']

export function usePartnerPaymentSourceConfig(enabled = true) {
  return useQuery({
    queryKey: ['partner', 'payment-source-config'],
    queryFn: async () => (await api.get<PartnerPaymentSourceConfig>('/partner/payment-source-config')).data,
    enabled,
  })
}

export function usePutPartnerPaymentSource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PartnerPaymentSourcePayload) =>
      (await api.put('/partner/payment-source', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partner', 'payment-source-config']})
    },
  })
}

export function useDeletePartnerPaymentSource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.delete('/partner/payment-source')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['partner', 'payment-source-config']})
    },
  })
}
